import { Vec3 } from 'features/assetViz/utils/helpers'

export enum CardinalDirections {
  North = 'N',
  East = 'E',
  South = 'S',
  West = 'W',
}

/**
 * Compass component that places the directions N (North), E (East), S (South),
 * W (West) at 90 degree intervals from the starting orientation.
 * Assumes that the component covers a full rotation of 360 degrees.
 *
 * @param compassOrientationStart the angle (in degrees) that North should
 *                                  start at
 * @param plantNorthCompassOrientation an offset to apply to the compass orientation in degrees that is added onto the compass orientations start
 * @param getRotationPosition A function for calculating 3D compass position on the mesh surface
 * @param angleDeltaDegrees option to override the default 90 degree intervals with a different angle any angle that is a factor of 90 will
 *                   work and any angle that isn't a cardinal direction will be returned as degrees
 **/

export const getCompassAnglePositionData = (
  compassOrientationStart: number,
  getRotationPosition: (rotation: number) => Vec3,
  plantNorthCompassOrientation?: number,
  angleDeltaDegrees: 1 | 2 | 3 | 5 | 6 | 9 | 10 | 15 | 18 | 30 | 45 | 90 = 90,
  angleOffsetDegrees: number = 0
) => {
  //only handle the divisible by 90 case for now
  const normalizedAngleDeltaDegrees =
    90 % angleDeltaDegrees === 0 ? angleDeltaDegrees : 90
  const angleToCardinalDirectionMap = new Map([
    [0, CardinalDirections.North],
    [90, CardinalDirections.West],
    [180, CardinalDirections.South],
    [270, CardinalDirections.East],
  ])

  const cardinalDirections = (() => {
    //get the directions that need to be displayed on the compass positions

    const angleList = Array.from(
      { length: 360 / normalizedAngleDeltaDegrees },
      (_, i) => i * normalizedAngleDeltaDegrees
    )
    return angleList.map(
      //if the angle is in the map return the cardinal direction otherwise return 360 - the angle since angles are clockwise around assets
      (angle) => angleToCardinalDirectionMap.get(angle) ?? `${360 - angle}°`
    )
  })()
  const compassOrientationStartWithOffset = plantNorthCompassOrientation
    ? compassOrientationStart + plantNorthCompassOrientation
    : compassOrientationStart

  const compassAngularPos = cardinalDirections.map(
    (_, i) =>
      (i * normalizedAngleDeltaDegrees +
        compassOrientationStartWithOffset +
        360) %
      360
  )

  const compassPositions = compassAngularPos.map((rotation) =>
    getRotationPosition(rotation + angleOffsetDegrees)
  )

  return {
    normalizedAngleDeltaDegrees,
    cardinalDirections,
    compassAngularPos,
    compassPositions,
  }
}
